/**
 * Safe Area Hook
 *
 * Detects and handles safe areas for mobile devices including:
 * - Android virtual navigation buttons
 * - iOS home indicator
 * - Notches and dynamic islands
 * - Virtual keyboard
 */

import { useEffect, useState } from 'react';

interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface SafeAreaState {
  insets: SafeAreaInsets;
  viewportHeight: number;
  visualViewportHeight: number;
  hasVirtualKeyboard: boolean;
  hasVirtualNavButtons: boolean;
  isStandalone: boolean;
}

export const useSafeArea = () => {
  const [safeArea, setSafeArea] = useState<SafeAreaState>({
    insets: { top: 0, bottom: 0, left: 0, right: 0 },
    viewportHeight: window.innerHeight,
    visualViewportHeight: window.visualViewport?.height || window.innerHeight,
    hasVirtualKeyboard: false,
    hasVirtualNavButtons: false,
    isStandalone: false,
  });

  const updateSafeArea = () => {
    // Get CSS environment variables for safe areas (iOS and some Android)
    const getEnvValue = (variable: string) => {
      const value = getComputedStyle(document.documentElement).getPropertyValue(variable);
      return parseInt(value) || 0;
    };

    const top = getEnvValue('--safe-area-inset-top');
    const bottom = getEnvValue('--safe-area-inset-bottom');
    const left = getEnvValue('--safe-area-inset-left');
    const right = getEnvValue('--safe-area-inset-right');

    // Detect viewport dimensions
    const viewportHeight = window.innerHeight;
    const visualViewportHeight = window.visualViewport?.height || window.innerHeight;

    // Detect virtual keyboard (significant viewport height reduction)
    const hasVirtualKeyboard = visualViewportHeight < viewportHeight * 0.75;

    // Detect virtual navigation buttons (Android)
    // Usually causes a difference between screen.height and window.innerHeight
    const screenHeight = window.screen.height;
    const windowHeight = window.innerHeight;
    const heightDifference = screenHeight - windowHeight;

    // Android virtual nav buttons typically take 48-96px
    const hasVirtualNavButtons = heightDifference > 40 && heightDifference < 150;

    // Detect if running as standalone PWA
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone ||
      document.referrer.includes('android-app://');

    // If we detect virtual nav buttons but no bottom safe area, estimate it
    const estimatedBottomInset =
      hasVirtualNavButtons && bottom === 0
        ? Math.min(heightDifference, 72) // Common nav button height
        : bottom;

    setSafeArea({
      insets: {
        top,
        bottom: estimatedBottomInset,
        left,
        right,
      },
      viewportHeight,
      visualViewportHeight,
      hasVirtualKeyboard,
      hasVirtualNavButtons,
      isStandalone,
    });
  };

  useEffect(() => {
    // Initial calculation
    updateSafeArea();

    // Listen for viewport changes
    const handleResize = () => updateSafeArea();
    const handleVisualViewportChange = () => updateSafeArea();
    const handleOrientationChange = () => {
      // Delay to let orientation change complete
      setTimeout(updateSafeArea, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportChange);
      window.visualViewport.addEventListener('scroll', handleVisualViewportChange);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);

      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
        window.visualViewport.removeEventListener('scroll', handleVisualViewportChange);
      }
    };
  }, []);

  return {
    ...safeArea,
    // Helper functions
    getSafeAreaCSS: () => ({
      paddingTop: `${safeArea.insets.top}px`,
      paddingBottom: `${safeArea.insets.bottom}px`,
      paddingLeft: `${safeArea.insets.left}px`,
      paddingRight: `${safeArea.insets.right}px`,
    }),
    getContentHeight: () => {
      return safeArea.visualViewportHeight - safeArea.insets.top - safeArea.insets.bottom;
    },
  };
};
